import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AssetsReport = () => {
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState({ total: 0, totalPurchaseValue: 0, totalCurrentValue: 0, totalDepreciation: 0, byCategory: {} });
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchAssets(); }, []);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      const response = await apiClient.get(`/api/accounts/assets?${params.toString()}`);
      const data = Array.isArray(response.data) ? response.data : response.data;
      setAssets(data);
      calculateSummary(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (data) => {
    const totalPurchaseValue = data.reduce((sum, asset) => sum + (asset.purchase_value || 0), 0);
    const totalCurrentValue = data.reduce((sum, asset) => sum + (asset.current_value || 0), 0);
    const totalDepreciation = totalPurchaseValue - totalCurrentValue;
    const byCategory = data.reduce((acc, asset) => {
      if (!acc[asset.category]) {
        acc[asset.category] = { count: 0, purchaseValue: 0, currentValue: 0 };
      }
      acc[asset.category].count += 1;
      acc[asset.category].purchaseValue += asset.purchase_value || 0;
      acc[asset.category].currentValue += asset.current_value || 0;
      return acc;
    }, {});
    setSummary({ total: data.length, totalPurchaseValue, totalCurrentValue, totalDepreciation, byCategory });
  };

  const handleFilterApply = () => {
    fetchAssets();
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Assets Report</CardTitle>
          <div className="flex gap-4 items-end" data-testid="assets-report-filters">
            <div><Label>From Date</Label><Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="assets-from-date" /></div>
            <div><Label>To Date</Label><Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="assets-to-date" /></div>
            <Button onClick={handleFilterApply} disabled={loading} data-testid="assets-filter-btn"><FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Generate'}</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Assets</p>
              <p className="text-2xl font-bold text-blue-600">{summary.total}</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600">Purchase Value</p>
              <p className="text-2xl font-bold text-purple-600">₹{summary.totalPurchaseValue.toFixed(2)}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">Current Value</p>
              <p className="text-2xl font-bold text-green-600">₹{summary.totalCurrentValue.toFixed(2)}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Depreciation</p>
              <p className="text-2xl font-bold text-red-600">₹{summary.totalDepreciation.toFixed(2)}</p>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Assets by Category</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {Object.entries(summary.byCategory).map(([category, data]) => (
                <div key={category} className="p-4 border rounded-lg">
                  <p className="font-semibold">{category}</p>
                  <p className="text-sm text-gray-600">Count: {data.count}</p>
                  <p className="text-sm">Purchase: ₹{data.purchaseValue.toFixed(2)}</p>
                  <p className="text-sm text-green-600">Current: ₹{data.currentValue.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>

          <h3 className="text-lg font-semibold mb-3">All Assets</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3">Asset Code</th>
                <th className="text-left p-3">Asset Name</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Purchase Date</th>
                <th className="text-left p-3">Purchase Value</th>
                <th className="text-left p-3">Current Value</th>
                <th className="text-left p-3">Depreciation</th>
                <th className="text-left p-3">Location</th>
              </tr>
            </thead>
            <tbody>
              {assets.length === 0 ? (
                <tr><td colSpan="8" className="text-center p-4 text-gray-500">No assets found.</td></tr>
              ) : (
                assets.map((asset) => (
                  <tr key={asset.id} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium">{asset.asset_code}</td>
                    <td className="p-3">{asset.asset_name}</td>
                    <td className="p-3">{asset.category}</td>
                    <td className="p-3">{asset.purchase_date || '-'}</td>
                    <td className="p-3">₹{asset.purchase_value}</td>
                    <td className="p-3 text-green-600">₹{asset.current_value}</td>
                    <td className="p-3 text-red-600">₹{((asset.purchase_value || 0) - (asset.current_value || 0)).toFixed(2)}</td>
                    <td className="p-3">{asset.location || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AssetsReport;
